import apiClient from './api';
import {
  Product,
  Brand,
  Category,
  CreateProductRequest,
  UpdateProductRequest,
  ProductsResponse,
  CreateBrandRequest,
  CreateCategoryRequest,
  BulkUploadResult,
  ProductQueryParams,
} from '../types/api/products';

export class ProductsService {
  static async getProducts(params?: ProductQueryParams): Promise<ProductsResponse> {
    const response = await apiClient.get('/products/', { params });
    return response.data.products || response.data;
  }

  static async getProduct(id: number): Promise<Product> {
    const response = await apiClient.get(`/products/${id}/`);
    return response.data;
  }

  static async createProduct(data: CreateProductRequest): Promise<Product> {
    const formData = new FormData();
    
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    formData.append('brand_id', data.brand_id.toString());
    formData.append('category_id', data.category_id.toString());
    if (data.sku) formData.append('sku', data.sku);
    if (data.manufacturer) formData.append('manufacturer', data.manufacturer);
    if (data.country_of_origin) formData.append('country_of_origin', data.country_of_origin);
    if (data.price_range_min) formData.append('price_range_min', data.price_range_min.toString());
    if (data.price_range_max) formData.append('price_range_max', data.price_range_max.toString());

    if (data.images) {
      data.images.forEach((image) => {
        formData.append('images', image);
      });
    }

    const response = await apiClient.post('/products/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  static async updateProduct(data: UpdateProductRequest): Promise<Product> {
    const { id, ...updateData } = data;
    const response = await apiClient.patch(`/products/${id}/`, updateData);
    return response.data;
  }

  static async deleteProduct(id: number): Promise<void> {
    await apiClient.delete(`/products/${id}/`);
  }

  static async bulkUploadProducts(csvFile: File): Promise<BulkUploadResult> {
    const formData = new FormData();
    formData.append('csv_file', csvFile);

    const response = await apiClient.post('/products/bulk-upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.results;
  }

  static async getBrands(): Promise<Brand[]> {
    const response = await apiClient.get('/brands/');
    return response.data;
  }

  static async createBrand(data: CreateBrandRequest): Promise<Brand> {
    const response = await apiClient.post('/brands/', data);
    return response.data;
  }

  static async updateBrand(id: number, data: Partial<CreateBrandRequest>): Promise<Brand> {
    const response = await apiClient.patch(`/brands/${id}/`, data);
    return response.data;
  }

  static async deleteBrand(id: number): Promise<void> {
    await apiClient.delete(`/brands/${id}/`);
  }

  static async getCategories(): Promise<Category[]> {
    const response = await apiClient.get('/categories/');
    return response.data;
  }

  static async createCategory(data: CreateCategoryRequest): Promise<Category> {
    const response = await apiClient.post('/categories/', data);
    return response.data;
  }

  static async updateCategory(id: number, data: Partial<CreateCategoryRequest>): Promise<Category> {
    const response = await apiClient.patch(`/categories/${id}/`, data);
    return response.data;
  }

  static async deleteCategory(id: number): Promise<void> {
    await apiClient.delete(`/categories/${id}/`);
  }
}

export default ProductsService;